import { useEffect } from 'react';
import { STG_HOST, HBAS_HOST, MILKEN_HOST, TESTING_HOST } from '@/utils/constants';
import { init, push } from "@socialgouv/matomo-next";

interface MatomoConfig {
  trackerUrl: string;
  siteId: string;
}

const MATOMO_CONFIGS: Record<string, MatomoConfig> = {
  [STG_HOST]: {
    trackerUrl: '//matomo-prod.eba-tdrs4upp.us-east-1.elasticbeanstalk.com/',
    siteId: '1'
  },
  [HBAS_HOST]: {
    trackerUrl: '//matomo-prod.eba-tdrs4upp.us-east-1.elasticbeanstalk.com/',
    siteId: '2' // You may want to use different site IDs for different domains
  },
  [MILKEN_HOST]: {
    trackerUrl: '//matomo-prod.eba-tdrs4upp.us-east-1.elasticbeanstalk.com/',
    siteId: '3' // You may want to use different site IDs for different domains
  }
};

// <!-- Matomo -->
// <script>
//   var _paq = window._paq = window._paq || [];
//   /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
//   _paq.push(['trackPageView']);
//   _paq.push(['enableLinkTracking']);
//   (function() {
//     var u="//matomo-prod.eba-tdrs4upp.us-east-1.elasticbeanstalk.com/";
//     _paq.push(['setTrackerUrl', u+'matomo.php']);
//     _paq.push(['setSiteId', '1']);
//     var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
//     g.async=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
//   })();
// </script>
// <!-- End Matomo Code -->


const useMatomo = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const currentHost = window.location.host;
    const config = MATOMO_CONFIGS[currentHost];

    // Only initialize Matomo for configured domains
    if (!config) return;

    // Initialize _paq array if it doesn't exist
    window._paq = window._paq || [];

    // Configure Matomo tracking
    window._paq.push(['trackPageView']);
    window._paq.push(['enableLinkTracking']);
    window._paq.push(['setTrackerUrl', config.trackerUrl + 'matomo.php']);
    window._paq.push(['setSiteId', config.siteId]);

    // Check if Matomo script is already loaded
    if (document.querySelector('script[src*="matomo.js"]')) {
      return;
    }

    // Load Matomo script
    const script = document.createElement('script');
    const firstScript = document.getElementsByTagName('script')[0];
    script.async = true;
    script.src = config.trackerUrl + 'matomo.js';
    
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    }
  }, []);

  // Function to track custom events
  const trackEvent = (category: string, action: string, name?: string, value?: number) => {
    if (typeof window !== 'undefined' && window._paq) {
      window._paq.push(['trackEvent', category, action, name, value]);
    }
  };

  // Function to track page views manually
  const trackPageView = (customTitle?: string) => {
    if (typeof window !== 'undefined' && window._paq) {
      if (customTitle) {
        window._paq.push(['setDocumentTitle', customTitle]);
      }
      window._paq.push(['trackPageView']);
    }
  };

  return {
    trackEvent,
    trackPageView
  };
};

// Extend Window interface to include _paq
declare global {
  interface Window {
    _paq: any[];
  }
}

export default useMatomo;

const isDev = process.env.NODE_ENV === "development";
const MATOMO_ANALYTICS_URL = "//analytics.myconnect.ai/"
const MATOMO_LOCALHOST_URL = "//localhost:8080/"
const LOCALHOST_MATOMO_SITE_ID = "1" // adjust this according to your local matomo
const STAGING_MATOMO_SITE_ID = "1"
const HBAS_MATOMO_SITE_ID = "2"
const MILKEN_MATOMO_SITE_ID = "3"
const TESTING_MATOMO_SITE_ID = "4"

import { useRef } from "react";

export function useMatomoConfig() {
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const currentHost = window.location.hostname;
    let matomoConfig;

    if (isDev) {
      matomoConfig = {
        url: MATOMO_LOCALHOST_URL,
        siteId: LOCALHOST_MATOMO_SITE_ID,
      };
    } else {
      if (currentHost === STG_HOST) {
        matomoConfig = {
          url: MATOMO_ANALYTICS_URL,
          siteId: STAGING_MATOMO_SITE_ID,
        };
      } else if (currentHost === HBAS_HOST) {
        matomoConfig = {
          url: MATOMO_ANALYTICS_URL,
          siteId: HBAS_MATOMO_SITE_ID,
        };
      } else if (currentHost === MILKEN_HOST) {
        matomoConfig = {
          url: MATOMO_ANALYTICS_URL,
          siteId: MILKEN_MATOMO_SITE_ID,
        };
      } else if (currentHost === TESTING_HOST) {
        matomoConfig = {
          url: MATOMO_ANALYTICS_URL,
          siteId: TESTING_MATOMO_SITE_ID,
        };
      }
    }

    if (matomoConfig) {
      let additionalSettings = {};
      if (isDev) {
        additionalSettings = {
          onInitialization: () => {
            console.log("Matomo initialized ⭐");
          },
          onRouteChangeStart: (path: string) => console.log("Matomo - Route start:", path),
          onRouteChangeComplete: (path: string) => console.log("Matomo - Route end:", path),
        }
      }
      init({
        url: matomoConfig.url,
        siteId: matomoConfig.siteId,
        ...additionalSettings,
        onScriptLoadingError: () => {
          console.warn(
            `Matomo script failed to load for ${currentHost} - possible mixed content issue`,
          );
        },
      });
    }
  }, []);
}